import { Shift } from '@amzn/red-velvet-api';
import { ComplexSignupInputs } from '../../hooks/betterEvents';
import { useCreateSignup } from '../../hooks/shifts/shiftSignup';
import { useJoinWaitlist } from '../../hooks/shifts/shiftWaitlist';
import { VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP } from '../../utils/constants';

/** What a ComplexSignupDialog submits as. 'signup' is the default, unchanged behavior. */
export type ComplexSignupMode = 'signup' | 'waitlist';

export interface ComplexSignupSubmission {
  /** True in waitlist mode — the dialog suppresses the guests field and the "Spots available" line. */
  isWaitlist: boolean;
  /** Whether the active mutation (create-signup or join-waitlist) is in flight. */
  isPending: boolean;
  /** i18n key for the submit button label. */
  submitLabelKey: string;
  /** Analytics name for the submit button. */
  analyticsName: string;
  /** Submit the collected complex data in the active mode, invoking onSettled once the call settles. */
  submit: (data: ComplexSignupInputs, onSettled: () => void) => void;
}

/**
 * Encapsulates how ComplexSignupDialog submits in each mode, so the dialog body can stay a single
 * shared form rather than branching on the mode throughout. The two modes differ only at submission:
 *  - 'signup'   creates a signup (the full request — guests + the caller's alias).
 *  - 'waitlist' joins the shift's waitlist instead — no guests, no alias (the backend keys the entry
 *               off the caller) — collecting only t-shirt / questions / waiver for the eventual
 *               auto-signup.
 *
 * Both underlying mutations are keyed on (eventId, shiftId), so the existing useSignupAlerts /
 * useWaitlistAlerts / useLatestSignupMutationState consumers still match. Both hooks are called
 * unconditionally (rules of hooks); only the active one is ever mutated.
 */
export function useComplexSignupSubmission(
  mode: ComplexSignupMode,
  eventId: string,
  shift: Shift,
  userId: string
): ComplexSignupSubmission {
  const isWaitlist = mode === 'waitlist';
  const signupMutation = useCreateSignup(eventId, shift.shiftId);
  const joinWaitlistMutation = useJoinWaitlist(eventId, shift.shiftId);
  const activeMutation = isWaitlist ? joinWaitlistMutation : signupMutation;

  const submit = (data: ComplexSignupInputs, onSettled: () => void) => {
    if (isWaitlist) {
      // Join the waitlist with the collected complex data — no guests, no alias (the backend keys
      // the entry off the caller).
      joinWaitlistMutation.mutate(
        {
          tShirtSizeId: data.tShirtSizeId,
          waiverAccepted: data.waiverAccepted,
          questionAnswers: data.questionAnswers,
        },
        { onSettled }
      );
    } else {
      signupMutation.mutate(
        {
          eventId,
          shift,
          request: { ...data, shift: shift.shiftId, alias: userId },
        },
        { onSettled }
      );
    }
  };

  return {
    isWaitlist,
    isPending: activeMutation.isPending,
    submitLabelKey: isWaitlist
      ? 'eventDetails.shiftDisplay.joinWaitlist'
      : 'eventDetails.shiftDisplay.register',
    analyticsName: isWaitlist
      ? VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP
      : 'shifts_complex_signup',
    submit,
  };
}
